/**
 * The tenant's derived journey stage, as a pill.
 *
 * Extracted from AdminTenants so the tenant list, the room view's current
 * tenant card and the room history all render the SAME badge the tenant
 * profile header shows. Colours come from the shared stage metadata
 * (src/lib/tenantJourney.ts), so a stage can never be styled two ways.
 *
 * Nothing here reads `bookings.status` or `contract_status` directly: those
 * columns are claims, and rendering them beside the derived stage is what made
 * a room read "Active / signed" while the profile read "Key pickup pending".
 *
 * ARCHIVED is deliberately a SECOND chip, not a stage. `admin_archived_at` is
 * an orthogonal axis: an archived tenancy still sits at a real stage (it was
 * archived *at* Deposit pending, say), and collapsing the two would destroy
 * that. The Tenants filter lists "Archived" next to the stages, which makes it
 * look like one — it is not, and replacing the stage with it would lose
 * information every other surface needs.
 *
 * The chip also says WHY (COL-165). Archiving used to be something an admin did
 * by hand, so "Archived by an admin" was a safe thing to assert. It no longer
 * is: signing a contract now archives the losing applicants for that room and
 * the signer's own applications elsewhere, automatically. A reason-less chip
 * would make a system decision look like a colleague's. `admin_archive_reason`
 * is NULL for every row archived by hand and for everything predating COL-165,
 * which is exactly the case the original copy describes.
 */
import { useLanguage } from "@/contexts/LanguageContext";
import {
  TENANT_JOURNEY_STAGES,
  tenantJourneyStageMeta,
  tenantJourneyStageLabel,
  type TenantJourneyStageId,
} from "@/lib/tenantJourney";

/**
 * Why a booking was archived, when the system did it.
 * `bookings.admin_archive_reason`; NULL means an admin archived it by hand.
 */
export type TenantArchiveReason = "room_filled" | "signed_elsewhere";

/** The neutral chip that rides alongside the stage on an archived booking. */
export function TenantArchivedChip({
  reason,
  className,
}: {
  reason?: TenantArchiveReason | string | null;
  className?: string;
}) {
  const { t } = useLanguage();
  const { label, title } =
    reason === "room_filled"
      ? {
          label: t("admin.tenantJourney.archivedRoomFilled", "Room filled"),
          title: t(
            "admin.tenantJourney.archivedRoomFilledTitle",
            "Archived automatically — someone else signed for this room. The stage beside this is where the application had got to."
          ),
        }
      : reason === "signed_elsewhere"
        ? {
            label: t(
              "admin.tenantJourney.archivedSignedElsewhere",
              "Signed elsewhere"
            ),
            title: t(
              "admin.tenantJourney.archivedSignedElsewhereTitle",
              "Archived automatically — this applicant signed for a different room. The stage beside this is where the application had got to."
            ),
          }
        : {
            label: t("admin.tenantJourney.archived", "Archived"),
            title: t(
              "admin.tenantJourney.archivedTitle",
              "Archived by an admin — hidden from the live pipeline. The stage beside this is where the tenancy actually stands."
            ),
          };
  return (
    <span
      title={title}
      className={
        "inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-500/10 px-2.5 py-0.5 text-xs font-semibold text-slate-600 dark:border-slate-700 dark:bg-slate-500/15 dark:text-slate-300 " +
        (className ?? "")
      }
    >
      <span className="h-1.5 w-1.5 rounded-full bg-slate-400/80" />
      {label}
    </span>
  );
}

export function TenantJourneyStageBadge({
  stageId,
  terminalStatus,
  archived,
  archiveReason,
  className,
}: {
  stageId: TenantJourneyStageId;
  /**
   * When the deal ended as `cancelled`/`terminated` (never a real tenant), the
   * derived stage is "Moved out" — render a distinct terminal badge instead so
   * "Moved out" stays meaningful for genuine move-outs.
   */
  terminalStatus?: "moved_out" | "terminated" | "cancelled" | null;
  /** `admin_archived_at` is set — adds the Archived chip after the stage. */
  archived?: boolean;
  /** `admin_archive_reason` — names the reason on that chip when the system set it. */
  archiveReason?: TenantArchiveReason | string | null;
  className?: string;
}) {
  const { t } = useLanguage();
  if (terminalStatus === "cancelled" || terminalStatus === "terminated") {
    const label =
      terminalStatus === "cancelled"
        ? t("admin.tenantJourney.cancelled", "Cancelled")
        : t("admin.tenantJourney.terminated", "Terminated");
    return (
      <>
        <span
          className={
            "inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-500/10 px-2.5 py-0.5 text-xs font-semibold text-rose-700 dark:border-rose-800 dark:bg-rose-500/15 dark:text-rose-200 " +
            (className ?? "")
          }
        >
          {label}
        </span>
        {archived && <TenantArchivedChip reason={archiveReason} />}
      </>
    );
  }
  const s = tenantJourneyStageMeta(stageId);
  const step = TENANT_JOURNEY_STAGES.findIndex((x) => x.id === stageId) + 1;
  // The derived stage is the only thing shown. A `bookings.status` that claims
  // a later stage is deliberately NOT flagged here: the stage badge already
  // shows where the tenant actually is, and the extra "Status ahead" chip was
  // noise on every row.
  return (
    <>
      <span
        title={`Step ${step} of ${TENANT_JOURNEY_STAGES.length}`}
        className={
          "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold " +
          (className ?? "")
        }
        style={{ background: s.bg, color: s.text, borderColor: s.border }}
      >
        {tenantJourneyStageLabel(t, stageId)}
      </span>
      {archived && <TenantArchivedChip reason={archiveReason} />}
    </>
  );
}
